<template>
    <BootstrapModal
        modalSize="l"
        ref="contactDetailsRef"
        modalId="contact-details"
        :title="form.title || 'Contact Details'"
        :closeText="'Close'"
        :saveText="'Save to Contacts'"
        :loading="isLoading"
        @save="() => saveAsVcf(form)"
        @close="closeModalContactDetails"
    >
        <template #left-icon>
            <svg
                @click="showPopup = true"
                width="1.5rem"
                height="1.5rem"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
                <g
                    id="SVGRepo_tracerCarrier"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                ></g>
                <g id="SVGRepo_iconCarrier">
                    <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M16.5 2.25C14.7051 2.25 13.25 3.70507 13.25 5.5C13.25 5.69591 13.2673 5.88776 13.3006 6.07412L8.56991 9.38558C8.54587 9.4024 8.52312 9.42038 8.50168 9.43939C7.94993 9.00747 7.25503 8.75 6.5 8.75C4.70507 8.75 3.25 10.2051 3.25 12C3.25 13.7949 4.70507 15.25 6.5 15.25C7.25503 15.25 7.94993 14.9925 8.50168 14.5606C8.52312 14.5796 8.54587 14.5976 8.56991 14.6144L13.3006 17.9259C13.2673 18.1122 13.25 18.3041 13.25 18.5C13.25 20.2949 14.7051 21.75 16.5 21.75C18.2949 21.75 19.75 20.2949 19.75 18.5C19.75 16.7051 18.2949 15.25 16.5 15.25C15.4472 15.25 14.5113 15.7506 13.9174 16.5267L9.43806 13.3911C9.63809 12.9694 9.75 12.4978 9.75 12C9.75 11.5022 9.63809 11.0306 9.43806 10.6089L13.9174 7.4733C14.5113 8.24942 15.4472 8.75 16.5 8.75C18.2949 8.75 19.75 7.29493 19.75 5.5C19.75 3.70507 18.2949 2.25 16.5 2.25ZM14.75 5.5C14.75 4.5335 15.5335 3.75 16.5 3.75C17.4665 3.75 18.25 4.5335 18.25 5.5C18.25 6.4665 17.4665 7.25 16.5 7.25C15.5335 7.25 14.75 6.4665 14.75 5.5ZM6.5 10.25C5.5335 10.25 4.75 11.0335 4.75 12C4.75 12.9665 5.5335 13.75 6.5 13.75C7.4665 13.75 8.25 12.9665 8.25 12C8.25 11.0335 7.4665 10.25 6.5 10.25ZM16.5 16.75C15.5335 16.75 14.75 17.5335 14.75 18.5C14.75 19.4665 15.5335 20.25 16.5 20.25C17.4665 20.25 18.25 19.4665 18.25 18.5C18.25 17.5335 17.4665 16.75 16.5 16.75Z"
                        fill="#999"
                    ></path>
                </g>
            </svg>
        </template>

        <template #body>
            <div
                class="row mt-4 text-center"
                v-if="form.first_name || form.last_name"
            >
                <div class="col-12 d-flex flex-column align-items-center">
                    <h5 class="fw-semibold mb-1">
                        {{ form.first_name }} {{ form.last_name }}
                    </h5>
                    <h6
                        class="fw-semibold mb-0"
                        v-if="form.organization || form.position"
                    >
                        {{ form.organization }}
                        <span v-if="form.position">– {{ form.position }}</span>
                    </h6>
                </div>
            </div>

            <!-- Contact cards grid -->
            <div class="contact-grid mt-4">
                <!-- Phone -->
                <a v-if="form.phone" :href="phoneHref" class="contact-card contact-card--link" aria-label="Call phone">
                    <div class="contact-card__icon">
                        <span class="icon-circle"><IconPhone :size="18" /></span>
                    </div>
                    <div class="contact-card__content">
                        <div class="contact-card__title">{{ form.phone_type || 'Phone' }}</div>
                        <div class="contact-card__value d-flex align-items-center gap-2 flex-wrap">
                            <span v-if="flagEmoji" aria-hidden="true" class="contact-flag">{{ flagEmoji }}</span>
                            <span v-if="displayedPhoneCode" class="text-muted">{{ displayedPhoneCode }}</span>
                            <span class="text-dark">{{ form.phone }}</span>
                        </div>
                    </div>
                </a>

                <!-- Email -->
                <a v-if="form.email" :href="emailHref" class="contact-card contact-card--link" aria-label="Send email">
                    <div class="contact-card__icon">
                        <span class="icon-circle"><IconMail :size="18" /></span>
                    </div>
                    <div class="contact-card__content">
                        <div class="contact-card__title">{{ form.email_type || 'Email' }}</div>
                        <div class="contact-card__value">{{ form.email }}</div>
                    </div>
                </a>

                <!-- Address -->
                <div v-if="hasAddress" class="contact-card">
                    <div class="contact-card__icon">
                        <span class="icon-circle"><IconMapPin :size="18" /></span>
                    </div>
                    <div class="contact-card__content">
                        <div class="contact-card__title">Address</div>
                        <div class="contact-card__value">{{ hasAddress }}</div>
                    </div>
                </div>

                <!-- Hours -->
                <div v-if="form.open_hours" class="contact-card">
                    <div class="contact-card__icon">
                        <span class="icon-circle"><IconClock :size="18" /></span>
                    </div>
                    <div class="contact-card__content">
                        <div class="contact-card__title">Our hours</div>
                        <div class="contact-card__value">{{ form.open_hours }}</div>
                    </div>
                </div>

                <!-- Username/Profile link -->
                <a v-if="username" :href="`/${username}`" target="_blank" class="contact-card contact-card--link" aria-label="Open profile">
                    <div class="contact-card__icon">
                        <span class="icon-circle"><IconLink :size="18" /></span>
                    </div>
                    <div class="contact-card__content">
                        <div class="contact-card__title">Username</div>
                        <div class="contact-card__value">@{{ username }}</div>
                    </div>
                </a>
            </div>

            <div class="row mt-3 text-center">
                <div class="col-lg-12">
                    <AppButton width="100%" @buttonClick="saveAsVcf(form)" :ignore-plan-restriction="true" :black="true">
                        {{ textBtn }}
                    </AppButton>
                </div>
            </div>
        </template>
    </BootstrapModal>
    <SharePopup :show="showPopup"
                :username="username"
                :urlName="urlName"
                :shortCode="shortCode"
                :data="data"
                :DoEndpoint="DoEndpoint"
                :DoBucket="DoBucket"
                @close="showPopup = false" />
</template>

<script>
import AppButton from "@/Components/AppButton.vue";
import BootstrapModal from "@/Components/BootstrapModal.vue";
import { IconClock, IconLink, IconMail, IconMapPin, IconPhone } from "@tabler/icons-vue";
import SharePopup from "@/Components/SharePopup.vue";

export default {
    components: {
        SharePopup,
        AppButton,
        BootstrapModal,
        IconClock,
        IconLink,
        IconMail,
        IconMapPin,
        IconPhone,
    },
    data() {
        return {
            showPopup: false,
            shortCode: "",
            textBtn: "Save to Contacts",
            showShareMenu: false,
            isLoading: false,
            form: {},
            data: {},
        };
    },
    props: ["item", "username", "urlName", "DoEndpoint", "DoBucket"],
    computed: {
        hasAddress() {
            const parts = [
                this.form.street,
                this.form.city,
                this.form.postal_code,
                this.form.country,
            ];
            return parts.filter(Boolean).join(", ");
        },
        displayedPhoneCode() {
            // Prefer explicit phone country code saved from the form
            if (this.form && typeof this.form.phone_country_code === 'string' && this.form.phone_country_code.trim()) {
                return this.form.phone_country_code.trim();
            }
            // Fallback: extract from phone if it starts with +
            const p = (this.form && this.form.phone) ? String(this.form.phone).trim() : '';
            const match = p.match(/^\+\d{1,4}/);
            return match ? match[0] : '';
        },
        flagEmoji() {
            // Build emoji flag from ISO country code (e.g., 'US' -> 🇺🇸)
            const iso = (this.form && this.form.country) ? String(this.form.country).toUpperCase() : '';
            if (!iso || iso.length !== 2) return '';
            const A = 65; // 'A'
            try {
                const codePoints = [iso.charCodeAt(0), iso.charCodeAt(1)].map(c => 0x1F1E6 + (c - A));
                return String.fromCodePoint(...codePoints);
            } catch (e) {
                return '';
            }
        },
        emailHref() {
            const e = (this.form && this.form.email) ? String(this.form.email).trim() : '';
            return e ? `mailto:${e}` : null;
        },
        phoneHref() {
            const rawPhone = (this.form && this.form.phone) ? String(this.form.phone).trim() : '';
            const code = this.displayedPhoneCode ? String(this.displayedPhoneCode).trim() : '';
            let full = rawPhone;
            if (rawPhone && rawPhone.startsWith('+')) {
                full = rawPhone;
            } else if (code) {
                full = `${code}${rawPhone}`;
            }
            const sanitized = full.replace(/[^+\d]/g, '');
            return sanitized ? `tel:${sanitized}` : null;
        },
    },
    methods: {
        closeModalContactDetails() {
            this.$refs.contactDetailsRef.closeModal("contact-details");
        },
        openContactDetailsById(data) {
            this.shortCode = data.short_code;
            this.data = data;
            this.form = {};
            this.isLoading = true;
            this.$refs.contactDetailsRef.showModal("contact-details");

            setTimeout(() => {
                axios
                    .get(route("link.show.contact-details", { id: data.id }))
                    .then((response) => {
                        const res = response.data;

                        if (res.status === "success" && res.data) {
                            this.form = res.data;
                            this.form.title = data.title || "Contact Details"; // optional
                            console.log("Contact Loaded:", this.form);
                        } else {
                            console.warn("Unexpected response:", res);
                        }
                    })
                    .catch((error) => {
                        console.error("Failed to load contact details:", error);
                    })
                    .finally(() => {
                        this.isLoading = false;
                    });
            }, 1500); // fixed comment: it's a 1.5-second delay, not 3
        },
        saveAsVcf(form) {
            if (form) {
                const first = (form.first_name || '').trim();
                const last = (form.last_name || '').trim();
                const hasName = !!(first || last);
                const username = (this.username || '').trim();
                const baseUrl = (this.urlName || '').trim().replace(/\/$/, '');
                const origin = baseUrl || ((typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : '');
                const profilePath = username ? `/${username}` : '';
                const profileUrl = origin && profilePath ? `${origin}${profilePath}` : profilePath || '';

                let vcfData = `BEGIN:VCARD\nVERSION:3.0\n`;

                // Name fields (fallback to username if names are empty)
                const nLast = last || '';
                const nFirst = first || '';
                const fn = hasName ? `${first} ${last}`.trim() : (username ? username : 'Contact');
                vcfData += `N:${nLast};${nFirst};;;\n`;
                vcfData += `FN:${fn}\n`;

                // Optional organization and position
                if ((form.organization || '').trim()) {
                    vcfData += `ORG:${String(form.organization).trim()}\n`;
                }
                if ((form.position || '').trim()) {
                    vcfData += `TITLE:${String(form.position).trim()}\n`;
                }

                // Conditionally include Email
                const email = (form.email || '').trim();
                const emailType = (form.email_type || '').trim();
                if (email) {
                    const type = emailType ? `;TYPE=${emailType}` : '';
                    vcfData += `EMAIL${type}:${email}\n`;
                }

                // Conditionally include Phone (prepend country code if present and phone not already international)
                let phone = (form.phone || '').trim();
                const phoneCode = (form.phone_country_code || '').trim();
                const phoneType = (form.phone_type || '').trim();
                if (phone) {
                    if (!phone.startsWith('+') && phoneCode) {
                        phone = `${phoneCode}${phone}`;
                    }
                    const type = phoneType ? `;TYPE=${phoneType}` : '';
                    vcfData += `TEL${type}:${phone}\n`;
                }

                // Conditionally include Address when any of its parts exists
                const street = (form.street || '').trim();
                const city = (form.city || '').trim();
                const state = (form.state || '').trim();
                const postal = (form.postal_code || '').trim();
                const country = (form.country || '').trim();
                if (street || city || state || postal || country) {
                    // ADR: PO Box;Extended;Street;City;Region;PostalCode;Country
                    vcfData += `ADR;TYPE=HOME:;;${street};${city};${state};${postal};${country}\n`;
                }

                // Always include URL to public profile as per requirement
                if (profileUrl) {
                    vcfData += `URL:${profileUrl}\n`;
                }

                vcfData += `END:VCARD`;

                const baseName = (first || last)
                    ? `${first || ''}_${last || ''}`.replace(/\s+/g, '_').replace(/^_+|_+$/g, '')
                    : (username || 'contact');

                const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
                if (isIOS) {
                    // Navigate to a server-side endpoint that returns the VCF with
                    // Content-Type: text/vcard and Content-Disposition: inline.
                    // A real HTTP response bypasses every browser's download manager
                    // and lets iOS route text/vcard to the native Contacts app.
                    window.location.href = route('link.vcf.contact-details', {
                        id: this.data.id,
                        username: username || undefined,
                    });
                } else {
                    const blob = new Blob([vcfData], { type: 'text/vcard' });
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `${baseName}.vcf`;
                    link.click();
                    setTimeout(() => URL.revokeObjectURL(url), 100);
                }
            } else {
                // Silently ignore; requirement: no error if minimal info
                return;
            }
        },
    },
    expose: ["openContactDetailsById"],
};
</script>

<style scoped>
.contact-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem; /* tighter on mobile */
}

@media (min-width: 576px) {
  .contact-grid { gap: 1rem; grid-template-columns: repeat(2, 1fr); }
}
@media (min-width: 992px) {
  .contact-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.25rem; /* a bit more breathing room on desktop */
    max-width: 720px; /* keep content readable inside modal on wide screens */
    margin-inline: auto; /* center within modal */
  }
}

.contact-card {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  transition: box-shadow 160ms ease, transform 120ms ease, border-color 160ms ease, background-color 160ms ease;
  color: inherit;
}

.contact-card--link {
  text-decoration: none;
  cursor: pointer;
}

.contact-card--link:hover {
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
  border-color: rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
}

.contact-card--link:active {
  transform: translateY(0) scale(0.99);
}

.contact-card--link:focus-visible {
  outline: 3px solid rgba(13, 110, 253, 0.35); /* bootstrap primary-ish */
  outline-offset: 2px;
}

.contact-card__icon .icon-circle {
  width: 40px;
  height: 40px;
  border-radius: 999px;
  display: grid;
  place-items: center;
  background: rgba(13, 110, 253, 0.08); /* soft primary tint */
  color: #0d6efd; /* bootstrap primary */
}

.contact-card__content { flex: 1; min-width: 0; }

.contact-card__title {
  font-weight: 600; /* strong hierarchy */
  font-size: 0.9rem;
  line-height: 1.1;
  margin-bottom: 2px;
  color: #111827; /* near-black */
}

.contact-card__value {
  font-weight: 400;
  font-size: 0.95rem;
  color: #334155; /* slate-ish */
  word-break: break-word;
}

.contact-flag { font-size: 1.05rem; line-height: 1; }

/* Respect modal dark backgrounds if any (basic safeguard) */
:root[data-bs-theme='dark'] .contact-card {
  background: #0b0f14;
  border-color: rgba(255, 255, 255, 0.08);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
}
:root[data-bs-theme='dark'] .contact-card__title { color: #f1f5f9; }
:root[data-bs-theme='dark'] .contact-card__value { color: #cbd5e1; }
:root[data-bs-theme='dark'] .contact-card__icon .icon-circle { background: rgba(13,110,253,0.18); color: #6ea8fe; }
</style>
